import { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Search, Send, User as UserIcon, RefreshCw, ArrowLeft, Star } from "lucide-react";

const STATUS_FILTERS = [
    { key: "all", label: "Все" },
    { key: "awaiting", label: "Ожидают" },
    { key: "in_progress", label: "В работе" },
    { key: "answered", label: "Отвечено" },
    { key: "closed", label: "Закрыто" },
];

const STATUS_BADGE = {
    awaiting: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", label: "Ожидает" },
    in_progress: { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200", label: "В работе" },
    answered: { bg: "bg-indigo-50", text: "text-indigo-700", border: "border-indigo-200", label: "Отвечено" },
    closed: { bg: "bg-slate-100", text: "text-slate-600", border: "border-slate-200", label: "Закрыто" },
};

function StatusBadge({ status }) {
    const s = STATUS_BADGE[status] || STATUS_BADGE.awaiting;
    return (
        <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${s.bg} ${s.text} ${s.border}`}
            data-testid={`status-badge-${status}`}
        >
            {s.label}
        </span>
    );
}

function RatingDisplay({ value }) {
    if (!value) return null;
    return (
        <span
            className="inline-flex items-center gap-0.5 text-amber-500"
            title={`Оценка: ${value}/5`}
            data-testid="appeal-rating"
        >
            {[1, 2, 3, 4, 5].map((i) => (
                <Star
                    key={i}
                    className={`h-3.5 w-3.5 ${i <= value ? "fill-amber-400 stroke-amber-400" : "stroke-slate-300"}`}
                    strokeWidth={1.5}
                />
            ))}
        </span>
    );
}

function formatTime(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) return d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
    return d.toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit", year: "2-digit" });
}

export default function Appeals() {
    const [appeals, setAppeals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [search, setSearch] = useState("");
    const [selectedId, setSelectedId] = useState(null);
    const [selected, setSelected] = useState(null);
    const [reply, setReply] = useState("");
    const [sending, setSending] = useState(false);
    const [error, setError] = useState("");

    const loadAppeals = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (filter !== "all") params.status = filter;
            if (search.trim()) params.search = search.trim();
            const { data } = await api.get("/appeals", { params });
            setAppeals(data.items || []);
        } finally {
            setLoading(false);
        }
    }, [filter, search]);

    useEffect(() => {
        loadAppeals();
    }, [loadAppeals]);

    // auto refresh every 15s
    useEffect(() => {
        const t = setInterval(loadAppeals, 15000);
        return () => clearInterval(t);
    }, [loadAppeals]);

    useEffect(() => {
        if (!selectedId) {
            setSelected(null);
            return;
        }
        let cancelled = false;
        const fetchSelected = async () => {
            try {
                const { data } = await api.get(`/appeals/${selectedId}`);
                if (!cancelled) setSelected(data);
            } catch {
                /* ignore transient errors */
            }
        };
        fetchSelected();
        // poll the open appeal every 5s for new user messages
        const t = setInterval(fetchSelected, 5000);
        return () => {
            cancelled = true;
            clearInterval(t);
        };
    }, [selectedId]);

    const handleSelect = (appeal) => {
        setSelectedId(appeal.id);
        setReply("");
        setError("");
    };

    const handleSendReply = async (e) => {
        e.preventDefault();
        if (!reply.trim() || !selectedId) return;
        setSending(true);
        setError("");
        try {
            const { data } = await api.post(`/appeals/${selectedId}/reply`, { text: reply.trim() });
            setSelected(data);
            setReply("");
            setAppeals((prev) => prev.map((a) => (a.id === data.id ? data : a)));
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSending(false);
        }
    };

    const changeStatus = async (status) => {
        if (!selectedId) return;
        try {
            const { data } = await api.patch(`/appeals/${selectedId}`, { status });
            setSelected(data);
            setAppeals((prev) => prev.map((a) => (a.id === data.id ? data : a)));
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
    };

    return (
        <div className="h-[calc(100vh-3.5rem-4rem)] lg:h-screen flex flex-col" data-testid="appeals-page">
            {/* Header — hidden on mobile when a detail is open to free up space */}
            <div
                className={`px-4 lg:px-10 pt-4 lg:pt-8 pb-3 lg:pb-4 border-b border-slate-200 bg-white ${
                    selected ? "hidden lg:block" : ""
                }`}
            >
                <div className="flex items-center justify-between gap-4 flex-wrap mb-4 lg:mb-5">
                    <div>
                        <div className="text-xs uppercase tracking-[0.15em] text-slate-500 mb-1 lg:mb-2 hidden lg:block">
                            Кабинет
                        </div>
                        <h1 className="text-xl lg:text-3xl font-heading font-medium tracking-tight text-slate-900">
                            Обращения
                        </h1>
                    </div>
                    <button
                        onClick={loadAppeals}
                        data-testid="appeals-refresh"
                        className="inline-flex items-center gap-2 border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors rounded-md px-3 py-2 font-medium text-sm"
                    >
                        <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                        <span className="hidden sm:inline">Обновить</span>
                    </button>
                </div>

                <div className="flex items-center gap-3 flex-col sm:flex-row sm:flex-wrap">
                    <div className="flex items-center gap-1 bg-slate-100 rounded-md p-1 overflow-x-auto w-full sm:w-auto">
                        {STATUS_FILTERS.map((f) => (
                            <button
                                key={f.key}
                                onClick={() => setFilter(f.key)}
                                data-testid={`filter-${f.key}`}
                                className={`px-3 py-1.5 rounded-sm text-sm font-medium transition-colors whitespace-nowrap ${
                                    filter === f.key
                                        ? "bg-white text-slate-900 shadow-sm"
                                        : "text-slate-600 hover:text-slate-900"
                                }`}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>

                    <div className="relative w-full sm:flex-1 sm:min-w-[200px] sm:max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Поиск"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            data-testid="appeals-search"
                            className="w-full border border-slate-300 rounded-md pl-9 pr-3 py-2 text-sm bg-white placeholder:text-slate-400 focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none"
                        />
                    </div>
                </div>
            </div>

            <div className="flex-1 grid grid-cols-1 lg:grid-cols-[380px_1fr] min-h-0">
                {/* List — hidden on mobile when detail is open */}
                <aside
                    className={`border-r border-slate-200 bg-white overflow-y-auto ${
                        selected ? "hidden lg:block" : "block"
                    }`}
                    data-testid="appeals-list"
                >
                    {loading && appeals.length === 0 ? (
                        <div className="p-6 text-sm text-slate-500">Загрузка...</div>
                    ) : appeals.length === 0 ? (
                        <div className="p-6 text-sm text-slate-500" data-testid="appeals-empty">
                            Нет обращений по выбранному фильтру
                        </div>
                    ) : (
                        appeals.map((a) => {
                            const lastMsg = a.messages?.[a.messages.length - 1];
                            const isSelected = a.id === selectedId;
                            return (
                                <button
                                    key={a.id}
                                    onClick={() => handleSelect(a)}
                                    data-testid={`appeal-item-${a.id}`}
                                    className={`w-full text-left px-5 py-4 border-b border-slate-100 transition-colors ${
                                        isSelected ? "bg-slate-50" : "hover:bg-slate-50/60"
                                    }`}
                                >
                                    <div className="flex items-center justify-between gap-2 mb-1.5">
                                        <div className="font-medium text-sm text-slate-900 truncate flex items-center gap-2 min-w-0">
                                            <UserIcon className="h-3.5 w-3.5 text-slate-400 shrink-0" strokeWidth={1.5} />
                                            <span className="truncate">{a.tg_first_name || a.tg_username || `id ${a.tg_user_id}`}</span>
                                        </div>
                                        <div className="flex items-center gap-1.5 shrink-0">
                                            {a.rating ? <RatingDisplay value={a.rating} /> : null}
                                            <StatusBadge status={a.status} />
                                        </div>
                                    </div>
                                    <div className="text-sm text-slate-600 truncate">{lastMsg?.text || ""}</div>
                                    <div className="text-xs text-slate-400 mt-1.5">{formatTime(a.updated_at)}</div>
                                </button>
                            );
                        })
                    )}
                </aside>

                {/* Detail — full screen on mobile */}
                <section
                    className={`flex flex-col min-h-0 bg-slate-50 ${
                        selected ? "block" : "hidden lg:flex"
                    }`}
                    data-testid="appeals-detail"
                >
                    {!selected ? (
                        <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">
                            Выберите обращение слева, чтобы открыть его
                        </div>
                    ) : (
                        <>
                            {/* Detail header */}
                            <div className="bg-white border-b border-slate-200 px-4 lg:px-6 py-3 lg:py-4 flex items-start justify-between gap-3 flex-wrap">
                                <div className="flex items-start gap-3 min-w-0">
                                    <button
                                        onClick={() => {
                                            setSelectedId(null);
                                            setSelected(null);
                                        }}
                                        className="lg:hidden p-1.5 -ml-1 rounded-md hover:bg-slate-100 text-slate-700 mt-0.5 shrink-0"
                                        data-testid="appeals-back-button"
                                    >
                                        <ArrowLeft className="h-5 w-5" />
                                    </button>
                                    <div className="min-w-0">
                                        <div className="font-heading font-medium text-slate-900 truncate">
                                            {selected.tg_first_name || ""} {selected.tg_last_name || ""}
                                        </div>
                                        <div className="text-sm text-slate-500 truncate">
                                            {selected.tg_username ? `@${selected.tg_username} · ` : ""}
                                            ID {selected.tg_user_id}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 flex-wrap">
                                    {selected.rating ? <RatingDisplay value={selected.rating} /> : null}
                                    <StatusBadge status={selected.status} />
                                    {selected.status !== "in_progress" && selected.status !== "closed" && (
                                        <button
                                            onClick={() => changeStatus("in_progress")}
                                            data-testid="status-in-progress"
                                            className="text-xs px-3 py-1.5 border border-slate-300 hover:bg-slate-50 rounded-md transition-colors text-slate-700"
                                        >
                                            В работу
                                        </button>
                                    )}
                                    {selected.status !== "closed" && (
                                        <button
                                            onClick={() => changeStatus("closed")}
                                            data-testid="status-close"
                                            className="text-xs px-3 py-1.5 border border-slate-300 hover:bg-slate-50 rounded-md transition-colors text-slate-700"
                                        >
                                            Закрыть
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Thread */}
                            <div
                                className="flex-1 overflow-y-auto p-4 lg:p-6 space-y-4"
                                data-testid="appeals-thread"
                            >
                                {(selected.messages || []).map((m, i) => {
                                    const isUser = m.from === "user";
                                    return (
                                        <div key={i} className={`flex ${isUser ? "justify-start" : "justify-end"}`}>
                                            <div className="max-w-[85%] lg:max-w-[80%]">
                                                <div
                                                    className={`p-3 lg:p-4 text-sm whitespace-pre-wrap rounded-2xl ${
                                                        isUser
                                                            ? "bg-white border border-slate-200 text-slate-800 rounded-tl-none"
                                                            : "bg-blue-50 border border-blue-100 text-blue-900 rounded-tr-none"
                                                    }`}
                                                >
                                                    {m.text}
                                                </div>
                                                <div className={`text-xs text-slate-400 mt-1 ${isUser ? "" : "text-right"}`}>
                                                    {isUser ? "Абитуриент" : m.staff_name || "Сотрудник"} ·{" "}
                                                    {formatTime(m.created_at)}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Reply */}
                            {selected.status !== "closed" ? (
                                <form
                                    onSubmit={handleSendReply}
                                    className="bg-white border-t border-slate-200 p-3 lg:p-4 sticky bottom-0"
                                    data-testid="reply-form"
                                >
                                    {error && (
                                        <div
                                            className="mb-3 text-sm bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2"
                                            data-testid="reply-error"
                                        >
                                            {error}
                                        </div>
                                    )}
                                    <div className="flex items-end gap-2 lg:gap-3">
                                        <textarea
                                            value={reply}
                                            onChange={(e) => setReply(e.target.value)}
                                            placeholder="Введите ответ абитуриенту..."
                                            rows={2}
                                            data-testid="reply-textarea"
                                            className="flex-1 border border-slate-300 rounded-md px-3 py-2.5 text-sm bg-white placeholder:text-slate-400 focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none resize-none lg:rows-3"
                                        />
                                        <button
                                            type="submit"
                                            disabled={sending || !reply.trim()}
                                            data-testid="reply-send"
                                            className="inline-flex items-center gap-2 bg-brand text-white hover:bg-brand-hover disabled:opacity-60 transition-colors rounded-md px-3 lg:px-4 py-2.5 font-medium text-sm shrink-0"
                                        >
                                            <Send className="h-4 w-4" />
                                            <span className="hidden sm:inline">{sending ? "Отправка..." : "Отправить"}</span>
                                        </button>
                                    </div>
                                    <p className="text-xs text-slate-400 mt-2 hidden sm:block">
                                        Ответ автоматически отправится в Telegram. Диалог продолжается до закрытия обращения.
                                    </p>
                                </form>
                            ) : (
                                <div className="bg-slate-100 border-t border-slate-200 p-4 text-center text-sm text-slate-500">
                                    Обращение закрыто. Дальнейшая переписка невозможна.
                                </div>
                            )}
                        </>
                    )}
                </section>
            </div>
        </div>
    );
}
